import React from 'react';
import { GraduationCap, ClipboardCheck, TrendingUp } from 'lucide-react';

import { isStaffViewer } from '../Student/Dashboard/StudentDashboard2.jsx';
import { useAuth } from '../../../context/AuthContext';

import '../Trainer/TrainerDashboard.css';
import './ProgressCard.css';

/* =============================================================
   PROGRESS CARD (/progress-card)

   This page has no data source. There is no fetch here and there
   never has been.

   It used to carry six invented courses with invented marks, and an
   invented set of exam results, written as fixtures in this file and
   rendered as though they were the student's own record — including
   a course marked Failed. A student reads this page to find out how
   they are doing, so that is not a placeholder, it is a false
   statement about them. The fixtures are gone.

   What is left is the honest page: the three sections a progress
   card has, each saying plainly that nothing is recorded yet and
   what will make it appear.

   To finish this page, fetch the marks and render rows inside these
   three sections. Write the row markup against the real response
   rather than against a guessed shape — guessing the shape is how
   the fixtures got here.
   ============================================================= */

const SECTIONS = [
  {
    key: 'scores',
    icon: TrendingUp,
    title: 'Your progress',
    sub: 'Where you stand across the course so far',
    empty: 'No scores yet',
    text: 'Your attendance, assignment and assessment scores will appear here once your trainer publishes them.',
  },
  {
    key: 'courses',
    icon: GraduationCap,
    title: 'Course progress',
    empty: 'Nothing recorded yet',
    text: 'Each course you are enrolled in will appear here with its marks as they are graded.',
  },
  {
    key: 'exams',
    icon: ClipboardCheck,
    title: 'Examination results',
    empty: 'No results yet',
    text: 'Results appear here after each mock, assessment or project evaluation is graded.',
  },
];

export default function ProgressCard() {
  const { user } = useAuth();
  const isStaff = isStaffViewer(user);

  /* =============================================================
     STAFF VIEW — the admin and trainer kit
     ============================================================= */
  if (isStaff) {
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          {SECTIONS.map(({ key, icon: Icon, title, empty, text }) => (
            <section className="td-tbl" key={key}>
              <header className="td-tbl-head">
                <div>
                  <h5 className="td-tbl-title">{title}</h5>
                </div>
              </header>

              <div className="td-state">
                <span className="td-state-icon">
                  <Icon size={24} aria-hidden="true" />
                </span>
                <h6 className="td-state-title">{empty}</h6>
                <p className="td-state-text">{text}</p>
              </div>
            </section>
          ))}
        </div>
      </div>
    );
  }

  /* =============================================================
     STUDENT VIEW — the LinkedIn card language
     ============================================================= */
  return (
    <div className="dashboard-wrapper">
      <div className="pc">
        <div className="pc-wrap">
          {SECTIONS.map(({ key, icon: Icon, title, sub, empty, text }, i) => (
            <section className="pc-card" key={key}>
              <div className="pc-card-head">
                {i === 0 ? (
                  <h1 className="pc-card-title">
                    <Icon size={18} aria-hidden="true" />
                    {title}
                  </h1>
                ) : (
                  <h2 className="pc-card-title">
                    <Icon size={18} aria-hidden="true" />
                    {title}
                  </h2>
                )}
              </div>
              {sub && <p className="pc-card-sub">{sub}</p>}

              <div className="pc-empty">
                <span className="pc-empty-mark">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <h3 className="pc-empty-title">{empty}</h3>
                <p className="pc-empty-text">{text}</p>
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
